import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { motion } from "framer-motion";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import WeekelyTest from "../assests/WeekelyTest.webp";
import Kids from "../assests/kids_session.webp";

const weeklyTests = [
  "Chapter-wise tests every Saturday based on topics covered during the week.",
  "Monthly assessment exams covering all completed chapters.",
  "Question papers follow CBSE, ICSE, and State Board exam patterns.",
  "Objective, short-answer, and long-answer questions to build writing skills.",
  "Timed practice tests to improve speed and accuracy.",
  "Special revision tests before school exams and board examinations.",
  "Every paper personally evaluated by teachers with detailed feedback.",
  "Parent reports with marks, attendance, strengths, and improvement areas.",
];

const onlineOffline = [
  "Small-batch classes for personalized learning and individual attention.",
  "Smart board-based interactive teaching with concept-focused lessons.",
  "Daily doubt-clearing sessions conducted by experienced faculty.",
  "Comprehensive study materials, notes, and worksheets for every topic.",
  "Regular assignments and practice tests to strengthen concepts.",
  "Weekly quizzes with detailed performance analysis and feedback.",
  "Morning, evening, and weekend batches to suit every student's schedule.",
  "A disciplined, motivating, and student-friendly classroom environment.",
];

// Motion variants
const fadeUpVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const listContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15,
    },
  },
};

const listItemVariants = {
  hidden: { opacity: 0, x: -16 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

const MotionTypography = motion(Typography);
const MotionList = motion(List);
const MotionListItem = motion(ListItem);
const MotionGridItem = motion(Grid);

function ImageBlock({ src, alt }) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid #E2E8F0",
        height: "100%",
        minHeight: { xs: 240, sm: 330, md: 430 },
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
    </Box>
  );
}

function ContentBlock({
  icon,
  iconBg,
  iconColor,
  title,
  description,
  items,
  dotColor,
}) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        ></Box>

        <Typography
          sx={{
            fontSize: { xs: 24, sm: 28 },
            fontWeight: 800,
            color: "#1E3A8A",
          }}
        >
          {title}
        </Typography>
      </Box>

      <Typography
        sx={{
          color: "#64748B",
          lineHeight: 1.8,
          fontSize: { xs: 14, sm: 15 },
          mb: 3,
        }}
      >
        {description}
      </Typography>

      <MotionList
        disablePadding
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={listContainerVariants}
      >
        {items.map((item, index) => (
          <MotionListItem
            key={index}
            disablePadding
            variants={listItemVariants}
            sx={{
              alignItems: "flex-start",
              mb: 1.2,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 20,
                mt: "8px",
              }}
            >
              <FiberManualRecordIcon
                sx={{
                  fontSize: 8,
                  color: dotColor,
                }}
              />
            </ListItemIcon>

            <ListItemText
              primary={item}
              primaryTypographyProps={{
                sx: {
                  color: "#475569",
                  fontSize: { xs: 14, sm: 15 },
                  lineHeight: 1.7,
                },
              }}
            />
          </MotionListItem>
        ))}
      </MotionList>
    </Box>
  );
}

export default function WeeklyTestsAndClasses() {
  return (
    <Box
      sx={{
        py: { xs: 7, md: 10 },
        bgcolor: "#F8FAFC",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          width: "100%",
          maxWidth: "1200px",
          mx: "auto",
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Heading */}
        <MotionTypography
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={fadeUpVariants}
          sx={{
            color: "#2563EB",
            textTransform: "uppercase",
            letterSpacing: ".18em",
            fontWeight: "bold",
            fontSize: "14px",
            mb: 1,
          }}
        >
          Our Learning Process
        </MotionTypography>

        <MotionTypography
          variant="h3"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={fadeUpVariants}
          sx={{
            fontWeight: 800,
            color: "#1E3A8A",
            mb: 2,
          }}
        >
          Learning Beyond the Classroom
        </MotionTypography>

        <MotionTypography
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={fadeUpVariants}
          sx={{
            color: "#64748B",
            fontSize: { xs: 14, sm: 16 },

            lineHeight: 1.8,
          }}
        >
          Regular assessments and flexible learning methods help students
          strengthen concepts, improve academic performance, and stay confident
          throughout the school year.
        </MotionTypography>

        {/* Section 1 */}
        <Grid
          container
          spacing={{ xs: 3, md: 6 }}
          alignItems="center"
          sx={{ mb: { xs: 3, md: 10 } }}
        >
          <MotionGridItem
            size={{ xs: 12, md: 6 }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
              hidden: { opacity: 0, x: -50 },
              visible: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.6, ease: "easeOut" },
              },
            }}
          >
            <ContentBlock
              title="Weekly Tests"
              description="Regular assessments help students evaluate their understanding, identify improvement areas, and build confidence for school and board examinations."
              items={weeklyTests}
              dotColor="#16A34A"
            />
          </MotionGridItem>

          <MotionGridItem
            size={{ xs: 12, md: 6 }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
              hidden: { opacity: 0, x: 50 },
              visible: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.6, ease: "easeOut" },
              },
            }}
          >
            <ImageBlock src={WeekelyTest} alt="Weekly Tests" />
          </MotionGridItem>
        </Grid>

        {/* Section 2 */}
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
          <MotionGridItem
            size={{ xs: 12, md: 6 }}
            sx={{ order: { xs: 2, md: 0 } }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
              hidden: { opacity: 0, x: -50 },
              visible: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.6, ease: "easeOut" },
              },
            }}
          >
            <ImageBlock src={Kids} alt="Online Offline Classes" />
          </MotionGridItem>

          <MotionGridItem
            size={{ xs: 12, md: 6 }}
            sx={{ order: { xs: 1, md: 0 } }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
              hidden: { opacity: 0, x: 50 },
              visible: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.6, ease: "easeOut" },
              },
            }}
          >
            <ContentBlock
              title="Classes"
              description="Students receive quality education, personalized attention, and continuous academic support in every class."
              items={onlineOffline}
              dotColor="#2563EB"
            />
          </MotionGridItem>
        </Grid>
      </Container>
    </Box>
  );
}
